// Japanese Grammar Lessons Data
// Based on Genki I (Lessons 1-12) and Genki II (Lessons 13-23)

export const lessons = [
  {
    id: 1,
    title: "XはYです (X is Y)",
    book: "Genki I",
    chapter: 1,
    grammar: [
      { point: "は particle", explanation: "Topic marker - indicates what you're talking about" },
      { point: "です", explanation: "Polite copula - 'is/am/are'" },
      { point: "か particle", explanation: "Question marker at end of sentence" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I am a student.",
        japanese: "わたし___ がくせいです。",
        answer: "は",
        options: ["は", "が", "を", "に"],
        hint: "Use the topic marker particle",
        explanation: "は marks the topic of the sentence. わたし (I) is the topic being discussed."
      },
      {
        type: "fill",
        prompt: "Are you a teacher?",
        japanese: "あなたは せんせい___？",
        answer: "ですか",
        options: ["ですか", "です", "ました", "でした"],
        hint: "Questions need a question marker",
        explanation: "か at the end of a sentence makes it a question. です + か = ですか"
      },
      {
        type: "build",
        prompt: "This is a book.",
        words: ["は", "です", "これ", "ほん"],
        answer: ["これ", "は", "ほん", "です"],
        hint: "Basic structure: これ は [noun] です",
        explanation: "これ (this) は ほん (book) です - The topic marker は comes after the topic."
      },
      {
        type: "fill",
        prompt: "That is a pen.",
        japanese: "それ___ ペンです。",
        answer: "は",
        options: ["は", "が", "を", "も"],
        hint: "Mark the topic of the sentence",
        explanation: "それ (that) is the topic, so we use the topic marker は."
      },
      {
        type: "fill",
        prompt: "Is Mary a college student?",
        japanese: "メアリーさんは だいがくせい___？",
        answer: "ですか",
        options: ["ですか", "です", "じゃありません", "でした"],
        hint: "This is a question",
        explanation: "Questions in polite form end with ですか."
      },
      {
        type: "build",
        prompt: "I am not a teacher.",
        words: ["は", "じゃありません", "わたし", "せんせい"],
        answer: ["わたし", "は", "せんせい", "じゃありません"],
        hint: "Negative form of です is じゃありません",
        explanation: "じゃありません is the negative form of です (polite)."
      },
      {
        type: "fill",
        prompt: "Mr. Yamada is Japanese.",
        japanese: "やまださんは にほんじん___。",
        answer: "です",
        options: ["です", "ですか", "じゃありません", "は"],
        hint: "This is a statement, not a question",
        explanation: "Simple statements end with です in polite form."
      },
      {
        type: "build",
        prompt: "Are you a student?",
        words: ["は", "がくせい", "あなた", "ですか"],
        answer: ["あなた", "は", "がくせい", "ですか"],
        hint: "Question form: [topic] は [noun] ですか",
        explanation: "あなた は がくせい ですか - Question format with か at the end."
      },
      {
        type: "fill",
        prompt: "This is not a bag.",
        japanese: "これは かばん___。",
        answer: "じゃありません",
        options: ["じゃありません", "です", "ですか", "でした"],
        hint: "Use the negative form",
        explanation: "じゃありません negates the です statement."
      },
      {
        type: "build",
        prompt: "Takeshi is a college student.",
        words: ["は", "だいがくせい", "たけしさん", "です"],
        answer: ["たけしさん", "は", "だいがくせい", "です"],
        hint: "Follow the pattern: [name]さん は [occupation] です",
        explanation: "Basic copula sentence: subject は predicate です."
      }
    ]
  },
  {
    id: 2,
    title: "これ/それ/あれ and の particle",
    book: "Genki I",
    chapter: 2,
    grammar: [
      { point: "これ/それ/あれ", explanation: "This/that/that over there" },
      { point: "の particle", explanation: "Possessive marker or connector" },
      { point: "だれの", explanation: "Whose" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "This is my book.",
        japanese: "これは わたし___ ほんです。",
        answer: "の",
        options: ["の", "は", "が", "を"],
        hint: "Use the possessive particle",
        explanation: "の indicates possession. わたしの means 'my' (literally: of me)."
      },
      {
        type: "build",
        prompt: "Whose bag is that?",
        words: ["は", "それ", "の", "だれ", "です", "かばん", "か"],
        answer: ["それ", "は", "だれ", "の", "かばん", "です", "か"],
        hint: "だれの means 'whose'",
        explanation: "それ は だれの かばん ですか - Question asking about ownership."
      },
      {
        type: "fill",
        prompt: "That over there is Tanaka-san's car.",
        japanese: "あれは たなかさん___ くるまです。",
        answer: "の",
        options: ["の", "は", "が", "に"],
        hint: "Show possession with の",
        explanation: "たなかさんの くるま = Tanaka-san's car. の connects the possessor to the possessed."
      },
      {
        type: "fill",
        prompt: "This is a Japanese language textbook.",
        japanese: "これは にほんご___ きょうかしょです。",
        answer: "の",
        options: ["の", "は", "を", "で"],
        hint: "の can connect nouns to describe type",
        explanation: "にほんごの きょうかしょ = Japanese (language) textbook. の connects descriptive noun to main noun."
      },
      {
        type: "build",
        prompt: "That over there is not my umbrella.",
        words: ["は", "あれ", "わたしの", "かさ", "じゃありません"],
        answer: ["あれ", "は", "わたしの", "かさ", "じゃありません"],
        hint: "あれ points to something far from both speaker and listener",
        explanation: "あれ (that over there) is used for objects distant from both people."
      },
      {
        type: "fill",
        prompt: "Is this your dictionary?",
        japanese: "これは あなた___ じしょですか。",
        answer: "の",
        options: ["の", "は", "が", "を"],
        hint: "Possessive particle",
        explanation: "あなたの = your (belonging to you)"
      },
      {
        type: "build",
        prompt: "Whose pencil is this?",
        words: ["これは", "だれの", "えんぴつ", "ですか"],
        answer: ["これは", "だれの", "えんぴつ", "ですか"],
        hint: "これ refers to something close to the speaker",
        explanation: "これは だれの えんぴつ ですか - Asking whose pencil is near the speaker."
      },
      {
        type: "fill",
        prompt: "That is Mary's cell phone.",
        japanese: "それは メアリーさん___ けいたいです。",
        answer: "の",
        options: ["の", "は", "を", "が"],
        hint: "Show possession",
        explanation: "メアリーさんの けいたい = Mary's cell phone"
      },
      {
        type: "build",
        prompt: "This is an English newspaper.",
        words: ["これは", "えいご", "の", "しんぶん", "です"],
        answer: ["これは", "えいご", "の", "しんぶん", "です"],
        hint: "の connects the language to the type of newspaper",
        explanation: "えいごの しんぶん = English (language) newspaper"
      },
      {
        type: "fill",
        prompt: "Is that over there Sue's bag?",
        japanese: "あれは スーさん___ かばんですか。",
        answer: "の",
        options: ["の", "は", "が", "も"],
        hint: "Possessive marker",
        explanation: "スーさんの かばん = Sue's bag. の shows possession."
      }
    ]
  },
  {
    id: 3,
    title: "Verb Conjugation (present tense)",
    book: "Genki I",
    chapter: 3,
    grammar: [
      { point: "ます form", explanation: "Polite present/future tense" },
      { point: "ません", explanation: "Polite negative present/future" },
      { point: "を particle", explanation: "Direct object marker" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I eat breakfast.",
        japanese: "わたしは あさごはん___ たべます。",
        answer: "を",
        options: ["を", "は", "が", "に"],
        hint: "Mark the direct object",
        explanation: "を marks the direct object. あさごはん (breakfast) is what is being eaten."
      },
      {
        type: "fill",
        prompt: "I don't watch TV.",
        japanese: "わたしは テレビを み___。",
        answer: "ません",
        options: ["ません", "ます", "ました", "ない"],
        hint: "Use the negative present form",
        explanation: "ません is the polite negative form. みます → みません (don't watch)."
      },
      {
        type: "build",
        prompt: "I read books.",
        words: ["を", "ほん", "よみます", "わたしは"],
        answer: ["わたしは", "ほん", "を", "よみます"],
        hint: "Pattern: [subject] は [object] を [verb]",
        explanation: "わたしは ほんを よみます - Standard subject-object-verb order."
      },
      {
        type: "fill",
        prompt: "Do you listen to music?",
        japanese: "あなたは おんがくを ききます___。",
        answer: "か",
        options: ["か", "を", "は", "の"],
        hint: "Question marker",
        explanation: "か at the end makes it a question. おんがくを ききますか (do you listen to music?)."
      },
      {
        type: "fill",
        prompt: "I drink coffee every morning.",
        japanese: "まいあさ コーヒーを のみ___。",
        answer: "ます",
        options: ["ます", "ません", "ました", "る"],
        hint: "Present/habitual action",
        explanation: "ます ending indicates present or habitual action in polite form."
      },
      {
        type: "build",
        prompt: "I don't study on weekends.",
        words: ["しゅうまつは", "べんきょう", "しません", "を"],
        answer: ["しゅうまつは", "べんきょう", "を", "しません"],
        hint: "Negative form of します",
        explanation: "べんきょうを します (do study) → しません (don't do)"
      },
      {
        type: "fill",
        prompt: "I go to school.",
        japanese: "がっこうに いき___。",
        answer: "ます",
        options: ["ます", "ません", "ました", "る"],
        hint: "Polite present form",
        explanation: "いく → いきます (go/will go) - polite present/future tense."
      },
      {
        type: "build",
        prompt: "Do you drink sake?",
        words: ["おさけを", "のみます", "か", "あなたは"],
        answer: ["あなたは", "おさけを", "のみます", "か"],
        hint: "Question form with か at the end",
        explanation: "あなたは おさけを のみますか - Polite question about habitual action."
      },
      {
        type: "fill",
        prompt: "I don't speak Japanese.",
        japanese: "にほんごを はなし___。",
        answer: "ません",
        options: ["ません", "ます", "ました", "ない"],
        hint: "Negative present form",
        explanation: "はなします → はなしません (don't speak)"
      },
      {
        type: "build",
        prompt: "I write letters.",
        words: ["てがみを", "かきます", "わたしは"],
        answer: ["わたしは", "てがみを", "かきます"],
        hint: "Standard verb sentence pattern",
        explanation: "わたしは てがみを かきます - I write letters."
      }
    ]
  },
  {
    id: 4,
    title: "に and で particles (time, location)",
    book: "Genki I",
    chapter: 4,
    grammar: [
      { point: "に particle (time)", explanation: "Indicates specific time" },
      { point: "に particle (destination)", explanation: "Direction of movement" },
      { point: "で particle", explanation: "Location of action/means" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I go to school at 8 o'clock.",
        japanese: "はちじ___ がっこうに いきます。",
        answer: "に",
        options: ["に", "で", "を", "へ"],
        hint: "Use に for specific time points",
        explanation: "に marks specific times like hours, days, or dates. はちじに (at 8 o'clock)."
      },
      {
        type: "fill",
        prompt: "I study at the library.",
        japanese: "としょかん___ べんきょうします。",
        answer: "で",
        options: ["で", "に", "を", "へ"],
        hint: "で marks the location where an action takes place",
        explanation: "で indicates the location of an action. としょかんで べんきょうします (study at the library)."
      },
      {
        type: "build",
        prompt: "I go to Japan.",
        words: ["に", "にほん", "いきます", "わたしは"],
        answer: ["わたしは", "にほん", "に", "いきます"],
        hint: "に marks destination with movement verbs",
        explanation: "に marks the destination of movement. わたしは にほんに いきます (I go to Japan)."
      },
      {
        type: "fill",
        prompt: "I watch TV at home.",
        japanese: "うち___ テレビを みます。",
        answer: "で",
        options: ["で", "に", "を", "へ"],
        hint: "Location of action",
        explanation: "で marks where an action takes place. うちで (at home) is where you watch TV."
      },
      {
        type: "fill",
        prompt: "I get up at 7 o'clock.",
        japanese: "しちじ___ おきます。",
        answer: "に",
        options: ["に", "で", "を", "が"],
        hint: "Specific time marker",
        explanation: "に marks specific times. しちじに おきます (get up at 7 o'clock)."
      },
      {
        type: "build",
        prompt: "I eat lunch at a restaurant.",
        words: ["レストラン", "で", "たべます", "ひるごはんを"],
        answer: ["レストラン", "で", "ひるごはんを", "たべます"],
        hint: "で for action location",
        explanation: "レストランで ひるごはんを たべます - eating happens at the restaurant location."
      },
      {
        type: "fill",
        prompt: "I returned to my room.",
        japanese: "へや___ かえりました。",
        answer: "に",
        options: ["に", "で", "を", "へ"],
        hint: "Destination with movement verb",
        explanation: "に marks the destination. へやに かえる (return to the room) uses に for destination."
      },
      {
        type: "build",
        prompt: "I read books at the library every day.",
        words: ["まいにち", "としょかん", "で", "ほんを", "よみます"],
        answer: ["まいにち", "としょかん", "で", "ほんを", "よみます"],
        hint: "Time + location で + object を + verb",
        explanation: "としょかんで (at the library) marks where the reading action takes place."
      },
      {
        type: "fill",
        prompt: "I will go to the park on Saturday.",
        japanese: "どようび___ こうえんに いきます。",
        answer: "に",
        options: ["に", "で", "を", "は"],
        hint: "Specific day marker",
        explanation: "に marks specific days of the week. どようびに (on Saturday)."
      },
      {
        type: "build",
        prompt: "I meet my friends at a cafe.",
        words: ["カフェ", "で", "あいます", "ともだちに"],
        answer: ["カフェ", "で", "ともだちに", "あいます"],
        hint: "Location で + person に + verb",
        explanation: "カフェで (at the cafe) is the location. ともだちに (with friends) uses に with あう verb."
      }
    ]
  },
  {
    id: 5,
    title: "Adjectives (い-adjectives & な-adjectives)",
    book: "Genki I",
    chapter: 5,
    grammar: [
      { point: "い-adjectives", explanation: "Adjectives ending in い that conjugate" },
      { point: "な-adjectives", explanation: "Adjectives that need な before nouns" },
      { point: "とても/あまり", explanation: "Very/not very (with negative)" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "This book is interesting.",
        japanese: "この ほんは おもしろ___。",
        answer: "いです",
        options: ["いです", "です", "なです", "いません"],
        hint: "い-adjectives end with い in their dictionary form",
        explanation: "おもしろい is an い-adjective. In polite speech, it becomes おもしろいです."
      },
      {
        type: "fill",
        prompt: "That is a quiet place.",
        japanese: "それは しずか___ ところです。",
        answer: "な",
        options: ["な", "の", "い", "で"],
        hint: "な-adjectives need な before nouns",
        explanation: "しずか is a な-adjective. When modifying a noun, add な: しずかな ところ (quiet place)."
      },
      {
        type: "build",
        prompt: "The room is not clean.",
        words: ["は", "へや", "じゃありません", "きれい"],
        answer: ["へや", "は", "きれい", "じゃありません"],
        hint: "な-adjective negation uses じゃありません",
        explanation: "きれい is a な-adjective. Negative: きれい じゃありません (is not clean)."
      },
      {
        type: "fill",
        prompt: "This movie is very fun.",
        japanese: "この えいがは とても たのし___。",
        answer: "いです",
        options: ["いです", "です", "なです", "な"],
        hint: "たのしい is an い-adjective",
        explanation: "たのしい (fun) is an い-adjective. とても (very) intensifies it. たのしいです in polite form."
      },
      {
        type: "fill",
        prompt: "My teacher is kind.",
        japanese: "わたしの せんせいは しんせつ___。",
        answer: "です",
        options: ["です", "いです", "な", "の"],
        hint: "な-adjectives as predicates use です",
        explanation: "しんせつ is a な-adjective. As a predicate (end of sentence), use です: しんせつです."
      },
      {
        type: "build",
        prompt: "This is not an expensive restaurant.",
        words: ["は", "これ", "たかい", "じゃありません", "レストラン"],
        answer: ["これ", "は", "たかい", "レストラン", "じゃありません"],
        hint: "い-adjective directly modifies the noun",
        explanation: "たかい (expensive) is an い-adjective that directly modifies レストラン without な."
      },
      {
        type: "fill",
        prompt: "Tokyo is not very quiet.",
        japanese: "とうきょうは あまり しずか___。",
        answer: "じゃありません",
        options: ["じゃありません", "です", "いです", "くないです"],
        hint: "あまり is used with negatives",
        explanation: "あまり (not very) requires negative form. しずか じゃありません (is not quiet)."
      },
      {
        type: "fill",
        prompt: "This bag is not heavy.",
        japanese: "この かばんは おもく___。",
        answer: "ないです",
        options: ["ないです", "ありません", "です", "いです"],
        hint: "い-adjective negative: drop い, add くない",
        explanation: "おもい → おもくない (not heavy). Polite: おもくないです."
      },
      {
        type: "build",
        prompt: "That is a famous temple.",
        words: ["それは", "ゆうめい", "な", "おてら", "です"],
        answer: ["それは", "ゆうめい", "な", "おてら", "です"],
        hint: "な-adjective needs な before noun",
        explanation: "ゆうめい (famous) is a な-adjective. ゆうめいな おてら (famous temple)."
      },
      {
        type: "fill",
        prompt: "This test was not difficult.",
        japanese: "この テストは むずかしく___。",
        answer: "なかったです",
        options: ["なかったです", "ないです", "ありませんでした", "じゃありませんでした"],
        hint: "い-adjective past negative",
        explanation: "むずかしい → むずかしくない → むずかしくなかった (was not difficult). Polite: むずかしくなかったです."
      }
    ]
  },
  {
    id: 6,
    title: "Te-form and ~ている",
    book: "Genki I",
    chapter: 6,
    grammar: [
      { point: "て-form", explanation: "Connecting form for verbs" },
      { point: "~ています", explanation: "Progressive/continuous action or state" },
      { point: "~てください", explanation: "Please do..." }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "Please read this.",
        japanese: "これを よん___。",
        answer: "でください",
        options: ["でください", "ています", "でした", "てます"],
        hint: "Use て-form + ください for polite requests",
        explanation: "よむ → よんで (te-form). よんでください (please read). ~てください is the polite request form."
      },
      {
        type: "fill",
        prompt: "I am studying now.",
        japanese: "いま べんきょうし___。",
        answer: "ています",
        options: ["ています", "てください", "ます", "ました"],
        hint: "~ています shows ongoing action",
        explanation: "する → して (te-form). ~ています indicates progressive/continuous action: べんきょうしています (am studying)."
      },
      {
        type: "build",
        prompt: "Please wait a moment.",
        words: ["ちょっと", "ください", "まって"],
        answer: ["ちょっと", "まって", "ください"],
        hint: "ちょっと means 'a little/moment'",
        explanation: "まつ → まって (te-form). ちょっと まってください (please wait a moment)."
      },
      {
        type: "fill",
        prompt: "Please don't eat here.",
        japanese: "ここで たべ___。",
        answer: "ないでください",
        options: ["ないでください", "てください", "ています", "ません"],
        hint: "Negative request: ないで + ください",
        explanation: "たべる → たべない (negative) → たべないでください (please don't eat)."
      },
      {
        type: "fill",
        prompt: "My mother is working now.",
        japanese: "ははは いま はたらい___。",
        answer: "ています",
        options: ["ています", "てください", "ました", "ます"],
        hint: "Ongoing action",
        explanation: "はたらく → はたらいて (te-form). はたらいています indicates she is currently working."
      },
      {
        type: "build",
        prompt: "Please listen carefully.",
        words: ["よく", "きいて", "ください"],
        answer: ["よく", "きいて", "ください"],
        hint: "よく means 'well/carefully'",
        explanation: "きく → きいて (te-form). よく きいてください (please listen carefully)."
      },
      {
        type: "fill",
        prompt: "I am wearing a sweater.",
        japanese: "セーターを き___。",
        answer: "ています",
        options: ["ています", "てください", "ます", "ました"],
        hint: "State resulting from past action",
        explanation: "きる → きて (te-form). きています shows the current state of wearing clothes."
      },
      {
        type: "build",
        prompt: "Please teach me Japanese.",
        words: ["にほんごを", "おしえて", "ください"],
        answer: ["にほんごを", "おしえて", "ください"],
        hint: "おしえる = to teach",
        explanation: "おしえる → おしえて (te-form). にほんごを おしえてください (please teach me Japanese)."
      },
      {
        type: "fill",
        prompt: "He knows Professor Yamada.",
        japanese: "かれは やまだせんせいを しっ___。",
        answer: "ています",
        options: ["ています", "てください", "ます", "ました"],
        hint: "しっています expresses 'to know'",
        explanation: "しる → しって (te-form). しっています means 'to know' (state of having learned)."
      },
      {
        type: "build",
        prompt: "Please open the window.",
        words: ["まどを", "あけて", "ください"],
        answer: ["まどを", "あけて", "ください"],
        hint: "あける = to open",
        explanation: "あける → あけて (te-form). まどを あけてください (please open the window)."
      }
    ]
  },
  {
    id: 7,
    title: "Counting and ~たい",
    book: "Genki I",
    chapter: 7,
    grammar: [
      { point: "Counters", explanation: "Different counters for different objects" },
      { point: "~たい", explanation: "Want to do something" },
      { point: "~たくない", explanation: "Don't want to do something" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I want to eat sushi.",
        japanese: "すしを たべ___。",
        answer: "たいです",
        options: ["たいです", "ています", "ました", "ます"],
        hint: "~たい expresses desire to do something",
        explanation: "たべる → たべたい (want to eat). ~たいです is the polite form expressing desire."
      },
      {
        type: "fill",
        prompt: "I don't want to go.",
        japanese: "いき___。",
        answer: "たくないです",
        options: ["たくないです", "ません", "たいです", "ませんでした"],
        hint: "Negative desire: ~たくない",
        explanation: "いく → いきたい → いきたくない (don't want to go). たくないです is the polite negative."
      },
      {
        type: "build",
        prompt: "I want to drink coffee.",
        words: ["を", "コーヒー", "のみたいです", "わたしは"],
        answer: ["わたしは", "コーヒー", "を", "のみたいです"],
        hint: "Standard word order with ~たい",
        explanation: "のむ → のみたい (want to drink). わたしは コーヒーを のみたいです."
      },
      {
        type: "fill",
        prompt: "I want to buy a new computer.",
        japanese: "あたらしい パソコンを かい___。",
        answer: "たいです",
        options: ["たいです", "ます", "ました", "たくないです"],
        hint: "Express desire with ~たい",
        explanation: "かう → かいたい (want to buy). かいたいです expresses desire to buy."
      },
      {
        type: "fill",
        prompt: "I don't want to study today.",
        japanese: "きょうは べんきょうし___。",
        answer: "たくないです",
        options: ["たくないです", "ません", "たいです", "ました"],
        hint: "Negative form of desire",
        explanation: "する → したい → したくない. べんきょうしたくないです (don't want to study)."
      },
      {
        type: "build",
        prompt: "I want to go to Japan next year.",
        words: ["らいねん", "にほんに", "いきたいです"],
        answer: ["らいねん", "にほんに", "いきたいです"],
        hint: "Time + destination + desire verb",
        explanation: "いく → いきたい (want to go). らいねん にほんに いきたいです."
      },
      {
        type: "fill",
        prompt: "I want to see that movie.",
        japanese: "その えいがを み___。",
        answer: "たいです",
        options: ["たいです", "ます", "ています", "たくないです"],
        hint: "Express wanting to see",
        explanation: "みる → みたい (want to see). みたいです expresses desire to watch/see."
      },
      {
        type: "build",
        prompt: "I don't want to wake up early tomorrow.",
        words: ["あした", "はやく", "おきたくないです"],
        answer: ["あした", "はやく", "おきたくないです"],
        hint: "Time + adverb + negative desire",
        explanation: "おきる → おきたい → おきたくない. あした はやく おきたくないです."
      },
      {
        type: "fill",
        prompt: "I want to meet my friends.",
        japanese: "ともだちに あい___。",
        answer: "たいです",
        options: ["たいです", "ます", "ました", "たくないです"],
        hint: "Desire to meet someone",
        explanation: "あう → あいたい (want to meet). ともだちに あいたいです."
      },
      {
        type: "build",
        prompt: "I don't want to do homework.",
        words: ["しゅくだいを", "したくないです"],
        answer: ["しゅくだいを", "したくないです"],
        hint: "Negative desire with する verb",
        explanation: "する → したい → したくない. しゅくだいを したくないです (don't want to do homework)."
      }
    ]
  },
  {
    id: 8,
    title: "Short Forms (plain form)",
    book: "Genki I",
    chapter: 8,
    grammar: [
      { point: "Plain present", explanation: "Casual present tense" },
      { point: "Plain past", explanation: "Casual past tense" },
      { point: "と思う", explanation: "I think that..." }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I think it will rain.",
        japanese: "あめが ふる___ おもいます。",
        answer: "と",
        options: ["と", "を", "が", "に"],
        hint: "と marks quoted thoughts",
        explanation: "と quotes the plain form verb before おもいます. ふると おもいます (I think it will rain)."
      },
      {
        type: "fill",
        prompt: "I think it's delicious. (casual)",
        japanese: "おいし___ と おもいます。",
        answer: "い",
        options: ["い", "いです", "かった", "くない"],
        hint: "Use plain form before と",
        explanation: "Before と おもいます, use plain form. おいしい (plain) not おいしいです. おいしいと おもいます."
      },
      {
        type: "build",
        prompt: "I think he will come.",
        words: ["くる", "と", "おもいます", "かれは"],
        answer: ["かれは", "くる", "と", "おもいます"],
        hint: "Plain form + と + おもいます",
        explanation: "くる (plain form) と おもいます. かれは くると おもいます (I think he will come)."
      },
      {
        type: "fill",
        prompt: "I don't think it's expensive.",
        japanese: "たか___ と おもいます。",
        answer: "くない",
        options: ["くない", "いです", "い", "くありません"],
        hint: "Plain negative form of い-adjective",
        explanation: "たかい → たかくない (plain negative). たかくないと おもいます."
      },
      {
        type: "fill",
        prompt: "I think she is a student.",
        japanese: "かのじょは がくせい___ と おもいます。",
        answer: "だ",
        options: ["だ", "です", "である", "な"],
        hint: "Plain copula for nouns",
        explanation: "です → だ (plain copula). がくせいだと おもいます (I think she's a student)."
      },
      {
        type: "build",
        prompt: "I think this book is interesting.",
        words: ["この", "ほんは", "おもしろい", "と", "おもいます"],
        answer: ["この", "ほんは", "おもしろい", "と", "おもいます"],
        hint: "Plain adjective form before と",
        explanation: "おもしろい (plain form) と おもいます. この ほんは おもしろいと おもいます."
      },
      {
        type: "fill",
        prompt: "I thought it was good.",
        japanese: "よかった___ おもいました。",
        answer: "と",
        options: ["と", "を", "が", "に"],
        hint: "Past thought still uses と",
        explanation: "よかった (plain past) と おもいました (I thought it was good)."
      },
      {
        type: "build",
        prompt: "I don't think he will go.",
        words: ["かれは", "いかない", "と", "おもいます"],
        answer: ["かれは", "いかない", "と", "おもいます"],
        hint: "Plain negative + と + おもいます",
        explanation: "いく → いかない (plain negative). かれは いかないと おもいます."
      },
      {
        type: "fill",
        prompt: "I think tomorrow will be sunny.",
        japanese: "あしたは はれる___ おもいます。",
        answer: "と",
        options: ["と", "を", "に", "が"],
        hint: "Quote marker と",
        explanation: "はれる (will be sunny) と おもいます. と marks the quoted thought."
      },
      {
        type: "build",
        prompt: "I thought the test was difficult.",
        words: ["テストは", "むずかしかった", "と", "おもいました"],
        answer: ["テストは", "むずかしかった", "と", "おもいました"],
        hint: "Plain past adjective + past thought",
        explanation: "むずかしい → むずかしかった (plain past). テストは むずかしかったと おもいました."
      }
    ]
  },
  {
    id: 9,
    title: "Past Tense",
    book: "Genki I",
    chapter: 9,
    grammar: [
      { point: "ました", explanation: "Polite past tense" },
      { point: "ませんでした", explanation: "Polite negative past" },
      { point: "Past tense adjectives", explanation: "かった for い-adj, でした for な-adj" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I went to school yesterday.",
        japanese: "きのう がっこうに いき___。",
        answer: "ました",
        options: ["ました", "ます", "ません", "ませんでした"],
        hint: "Polite past tense ending",
        explanation: "いく → いきます → いきました (went). ました is the polite past tense ending."
      },
      {
        type: "fill",
        prompt: "It was fun.",
        japanese: "たのし___。",
        answer: "かったです",
        options: ["かったです", "いです", "です", "でした"],
        hint: "い-adjective past tense",
        explanation: "たのしい → たのしかった (was fun). い-adjectives: drop い, add かった for past."
      },
      {
        type: "build",
        prompt: "I didn't eat breakfast.",
        words: ["を", "あさごはん", "たべませんでした", "わたしは"],
        answer: ["わたしは", "あさごはん", "を", "たべませんでした"],
        hint: "Negative past: ませんでした",
        explanation: "たべる → たべません → たべませんでした (didn't eat). Negative past uses ませんでした."
      },
      {
        type: "fill",
        prompt: "The weather was good yesterday.",
        japanese: "きのう てんきが よ___。",
        answer: "かったです",
        options: ["かったです", "いです", "です", "いました"],
        hint: "Past tense of いい",
        explanation: "いい (good) → よかった (was good). いい is irregular: よかったです in past tense."
      },
      {
        type: "fill",
        prompt: "I didn't watch TV last night.",
        japanese: "ゆうべ テレビを み___。",
        answer: "ませんでした",
        options: ["ませんでした", "ました", "ません", "ます"],
        hint: "Negative past verb form",
        explanation: "みる → みません → みませんでした (didn't watch). Negative past ending."
      },
      {
        type: "build",
        prompt: "The movie was interesting.",
        words: ["えいがは", "おもしろかったです"],
        answer: ["えいがは", "おもしろかったです"],
        hint: "い-adjective past form",
        explanation: "おもしろい → おもしろかった. えいがは おもしろかったです (the movie was interesting)."
      },
      {
        type: "fill",
        prompt: "My room was clean.",
        japanese: "わたしの へやは きれい___。",
        answer: "でした",
        options: ["でした", "かったです", "です", "ました"],
        hint: "な-adjective past uses でした",
        explanation: "きれい is a な-adjective. Past tense: きれいでした (was clean). な-adjectives use でした."
      },
      {
        type: "build",
        prompt: "I studied at the library yesterday.",
        words: ["きのう", "としょかんで", "べんきょうしました"],
        answer: ["きのう", "としょかんで", "べんきょうしました"],
        hint: "Past tense of します",
        explanation: "する → しました (did). べんきょうしました (studied) is past tense."
      },
      {
        type: "fill",
        prompt: "That restaurant wasn't delicious.",
        japanese: "その レストランは おいしく___。",
        answer: "なかったです",
        options: ["なかったです", "ありませんでした", "ないです", "かったです"],
        hint: "い-adjective negative past",
        explanation: "おいしい → おいしくない → おいしくなかった. おいしくなかったです (wasn't delicious)."
      },
      {
        type: "build",
        prompt: "I didn't buy anything yesterday.",
        words: ["きのう", "なにも", "かいませんでした"],
        answer: ["きのう", "なにも", "かいませんでした"],
        hint: "なにも with negative past",
        explanation: "かう → かいません → かいませんでした. なにも (nothing) requires negative verb."
      }
    ]
  },
  {
    id: 10,
    title: "Comparisons",
    book: "Genki I",
    chapter: 10,
    grammar: [
      { point: "より", explanation: "Than (for comparisons)" },
      { point: "~のほうが", explanation: "~ is more..." },
      { point: "いちばん", explanation: "Most/best" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "Coffee is more delicious than tea.",
        japanese: "コーヒーは おちゃ___ おいしいです。",
        answer: "より",
        options: ["より", "の", "が", "は"],
        hint: "より means 'than' in comparisons",
        explanation: "より marks the standard of comparison. コーヒーは おちゃより おいしいです (coffee is more delicious than tea)."
      },
      {
        type: "build",
        prompt: "Which do you like better?",
        words: ["どちらが", "すき", "です", "か"],
        answer: ["どちらが", "すき", "です", "か"],
        hint: "どちら means 'which (of two)'",
        explanation: "どちらが すきですか (which do you like?). どちら asks which of two options."
      },
      {
        type: "fill",
        prompt: "Summer is the hottest.",
        japanese: "なつが いちばん あつ___。",
        answer: "いです",
        options: ["いです", "です", "いました", "かったです"],
        hint: "いちばん + adjective = most/best",
        explanation: "いちばん (most) + あついです (hot). なつが いちばん あついです (summer is the hottest)."
      },
      {
        type: "fill",
        prompt: "Tokyo is bigger than Osaka.",
        japanese: "とうきょうは おおさか___ おおきいです。",
        answer: "より",
        options: ["より", "の", "が", "ほう"],
        hint: "Use より for comparison",
        explanation: "より indicates 'than'. とうきょうは おおさかより おおきいです (Tokyo is bigger than Osaka)."
      },
      {
        type: "build",
        prompt: "Which is more expensive?",
        words: ["どちらが", "もっと", "たかい", "です", "か"],
        answer: ["どちらが", "もっと", "たかい", "です", "か"],
        hint: "もっと means 'more'",
        explanation: "どちらが もっと たかいですか (which is more expensive?). もっと intensifies comparison."
      },
      {
        type: "fill",
        prompt: "Japanese is more difficult than English.",
        japanese: "にほんごは えいご___ むずかしいです。",
        answer: "より",
        options: ["より", "の", "が", "ほう"],
        hint: "Standard of comparison marker",
        explanation: "にほんごは えいごより むずかしいです. より marks what you're comparing against."
      },
      {
        type: "fill",
        prompt: "Winter is the coldest season.",
        japanese: "ふゆが いちばん さむい きせつ___。",
        answer: "です",
        options: ["です", "が", "より", "の"],
        hint: "Sentence ending for statement",
        explanation: "ふゆが いちばん さむい きせつです. です ends the statement about winter being coldest."
      },
      {
        type: "build",
        prompt: "This book is more interesting than that one.",
        words: ["この", "ほんは", "その", "ほんより", "おもしろいです"],
        answer: ["この", "ほんは", "その", "ほんより", "おもしろいです"],
        hint: "この vs その comparison",
        explanation: "この ほんは その ほんより おもしろいです. この (this) compared to その (that)."
      },
      {
        type: "fill",
        prompt: "Which do you like better, meat or fish?",
        japanese: "にくと さかなと どちら___ すきですか。",
        answer: "が",
        options: ["が", "を", "は", "の"],
        hint: "Subject marker in questions",
        explanation: "どちらが marks the subject of comparison. にくと さかなと どちらが すきですか."
      },
      {
        type: "build",
        prompt: "Math is the most difficult subject.",
        words: ["すうがくが", "いちばん", "むずかしい", "かもくです"],
        answer: ["すうがくが", "いちばん", "むずかしい", "かもくです"],
        hint: "Superlative with いちばん",
        explanation: "すうがくが いちばん むずかしい かもくです. いちばん indicates the superlative (most difficult)."
      }
    ]
  },
  {
    id: 11,
    title: "~たことがある (experience)",
    book: "Genki I",
    chapter: 11,
    grammar: [
      { point: "~たことがある", explanation: "Have done something before" },
      { point: "~たり~たりする", explanation: "Do things like... (listing examples)" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I have been to Japan.",
        japanese: "にほんに いった___ があります。",
        answer: "こと",
        options: ["こと", "とき", "もの", "の"],
        hint: "こと refers to an experience or abstract thing",
        explanation: "~たことがあります means 'have done before'. いったことがあります (have been/gone before)."
      },
      {
        type: "build",
        prompt: "Have you ever eaten sushi?",
        words: ["すしを", "たべた", "ことが", "あります", "か"],
        answer: ["すしを", "たべた", "ことが", "あります", "か"],
        hint: "Question about past experience",
        explanation: "たべたことがありますか asks about the experience of eating. すしを たべたことが ありますか."
      },
      {
        type: "fill",
        prompt: "I have never climbed Mt. Fuji.",
        japanese: "ふじさんに のぼった___ がありません。",
        answer: "こと",
        options: ["こと", "とき", "もの", "ところ"],
        hint: "こと for experience, negative with ありません",
        explanation: "~たことがありません means 'have never done'. のぼったことがありません (have never climbed)."
      },
      {
        type: "fill",
        prompt: "Have you ever seen a sumo match?",
        japanese: "すもうを み___ ことが ありますか。",
        answer: "た",
        options: ["た", "て", "る", "ます"],
        hint: "Past tense plain form before こと",
        explanation: "みる → みた (plain past). みたことがありますか (have you ever seen?)."
      },
      {
        type: "build",
        prompt: "I have been to Kyoto three times.",
        words: ["きょうとに", "さんかい", "いった", "ことが", "あります"],
        answer: ["きょうとに", "さんかい", "いった", "ことが", "あります"],
        hint: "Counter さんかい (three times) with experience",
        explanation: "きょうとに さんかい いったことが あります. Indicates multiple past experiences."
      },
      {
        type: "fill",
        prompt: "I have never ridden a bullet train.",
        japanese: "しんかんせんに のった___ がありません。",
        answer: "こと",
        options: ["こと", "とき", "もの", "ところ"],
        hint: "Negative experience pattern",
        explanation: "のる → のった (rode). のったことがありません (have never ridden)."
      },
      {
        type: "build",
        prompt: "Have you ever worn a kimono?",
        words: ["きものを", "きた", "ことが", "あります", "か"],
        answer: ["きものを", "きた", "ことが", "あります", "か"],
        hint: "きる (to wear) past experience question",
        explanation: "きる → きた. きものを きたことが ありますか (have you ever worn a kimono?)."
      },
      {
        type: "fill",
        prompt: "I have studied kanji before.",
        japanese: "かんじを べんきょうした___ があります。",
        answer: "こと",
        options: ["こと", "とき", "ひと", "もの"],
        hint: "Experience of studying",
        explanation: "べんきょうしたことがあります means 'have studied before/have the experience of studying'."
      },
      {
        type: "build",
        prompt: "I have never eaten natto.",
        words: ["なっとうを", "たべた", "ことが", "ありません"],
        answer: ["なっとうを", "たべた", "ことが", "ありません"],
        hint: "Negative experience statement",
        explanation: "たべたことがありません (have never eaten). なっとうを たべたことが ありません."
      },
      {
        type: "fill",
        prompt: "Have you ever met a famous person?",
        japanese: "ゆうめいじんに あった___ がありますか。",
        answer: "こと",
        options: ["こと", "とき", "ひと", "もの"],
        hint: "Question about meeting experience",
        explanation: "あう → あった. あったことがありますか asks about past experience of meeting."
      }
    ]
  },
  {
    id: 12,
    title: "~んです (explanation)",
    book: "Genki I",
    chapter: 12,
    grammar: [
      { point: "~んです", explanation: "Explanatory/seeking explanation" },
      { point: "Casual ~んだ", explanation: "Casual form of ~んです" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I'm tired. (explanatory)",
        japanese: "つかれている___。",
        answer: "んです",
        options: ["んです", "です", "ます", "でした"],
        hint: "んです adds explanatory nuance",
        explanation: "~んです (or ~のです) adds explanatory tone, giving context or reason. つかれているんです."
      },
      {
        type: "build",
        prompt: "Why are you studying?",
        words: ["どうして", "べんきょうして", "いるんです", "か"],
        answer: ["どうして", "べんきょうして", "いるんです", "か"],
        hint: "んです in questions seeks explanation",
        explanation: "どうして (why) + んですか seeks explanation. どうして べんきょうしているんですか."
      },
      {
        type: "fill",
        prompt: "What happened? (seeking explanation)",
        japanese: "どうした___か。",
        answer: "んです",
        options: ["んです", "です", "ます", "のです"],
        hint: "んです or のです both work",
        explanation: "どうしたんですか (what happened?) uses んです to seek explanation. んです = のです."
      },
      {
        type: "fill",
        prompt: "I don't feel well. (explaining)",
        japanese: "ちょうしが よくない___。",
        answer: "んです",
        options: ["んです", "です", "ます", "でした"],
        hint: "Explanatory tone for current state",
        explanation: "よくないんです explains why you might look unwell or need to leave. Adds context."
      },
      {
        type: "build",
        prompt: "Why didn't you come yesterday?",
        words: ["どうして", "きのう", "こなかったんです", "か"],
        answer: ["どうして", "きのう", "こなかったんです", "か"],
        hint: "Past tense + んです for explanation",
        explanation: "こなかった (didn't come) + んです. どうして きのう こなかったんですか seeks reason."
      },
      {
        type: "fill",
        prompt: "Where are you going? (seeking info)",
        japanese: "どこに いく___か。",
        answer: "んです",
        options: ["んです", "です", "ます", "でした"],
        hint: "んです shows interest in explanation",
        explanation: "どこに いくんですか. んです shows speaker wants to know context/reason for going."
      },
      {
        type: "build",
        prompt: "I have to go home now. (explaining)",
        words: ["もう", "かえらなければ", "ならないんです"],
        answer: ["もう", "かえらなければ", "ならないんです"],
        hint: "んです explains necessity",
        explanation: "ならないんです explains the obligation. もう かえらなければ ならないんです."
      },
      {
        type: "fill",
        prompt: "What are you looking for? (showing concern)",
        japanese: "なにを さがしている___か。",
        answer: "んです",
        options: ["んです", "です", "ます", "でした"],
        hint: "んです shows engagement/concern",
        explanation: "さがしているんですか uses んです to show interest and concern about what they're doing."
      },
      {
        type: "build",
        prompt: "I forgot my homework. (explaining situation)",
        words: ["しゅくだいを", "わすれたんです"],
        answer: ["しゅくだいを", "わすれたんです"],
        hint: "Past tense + んです for explanation",
        explanation: "わすれた (forgot) + んです explains the situation. しゅくだいを わすれたんです."
      },
      {
        type: "fill",
        prompt: "I don't have time. (explaining why can't do something)",
        japanese: "じかんが ない___。",
        answer: "んです",
        options: ["んです", "です", "ます", "でした"],
        hint: "Explanatory context for refusal",
        explanation: "ないんです explains why you can't do something. じかんが ないんです provides context/reason."
      }
    ]
  },
  {
    id: 13,
    title: "Potential Form (~できる)",
    book: "Genki II",
    chapter: 13,
    grammar: [
      { point: "Potential form", explanation: "Can do / Able to do something" },
      { point: "~が + potential", explanation: "Object marker changes to が with potential verbs" },
      { point: "見える/聞こえる", explanation: "Can see/hear (spontaneous potential)" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I can speak Japanese.",
        japanese: "にほんご___ はなせます。",
        answer: "が",
        options: ["が", "を", "に", "で"],
        hint: "Potential verbs take が not を",
        explanation: "With potential form, を changes to が. はなす → はなせる (can speak). にほんごが はなせます."
      },
      {
        type: "fill",
        prompt: "Can you read kanji?",
        japanese: "かんじが よ___か。",
        answer: "めます",
        options: ["めます", "みます", "める", "みる"],
        hint: "よむ potential form",
        explanation: "よむ → よめる (can read). Polite: よめます. かんじが よめますか (can you read kanji?)."
      },
      {
        type: "build",
        prompt: "I can't eat spicy food.",
        words: ["が", "からい", "たべられません", "もの"],
        answer: ["からい", "もの", "が", "たべられません"],
        hint: "Ichidan verb potential: られる",
        explanation: "たべる → たべられる (can eat). Negative: たべられません. からいものが たべられません."
      },
      {
        type: "fill",
        prompt: "I can see Mt. Fuji from here.",
        japanese: "ここから ふじさんが み___。",
        answer: "えます",
        options: ["えます", "ます", "える", "られます"],
        hint: "みえる is spontaneous potential",
        explanation: "みる → みえる (can see/is visible). みえます is the polite form. ふじさんが みえます."
      },
      {
        type: "fill",
        prompt: "Can you come tomorrow?",
        japanese: "あした こ___か。",
        answer: "られます",
        options: ["られます", "ます", "られる", "ません"],
        hint: "くる potential form is irregular",
        explanation: "くる → こられる (can come). Polite: こられます. あした こられますか (can you come tomorrow?)."
      },
      {
        type: "build",
        prompt: "I can swim.",
        words: ["およげます", "わたしは"],
        answer: ["わたしは", "およげます"],
        hint: "およぐ potential form",
        explanation: "およぐ (to swim) → およげる (can swim). わたしは およげます."
      },
      {
        type: "fill",
        prompt: "I can hear music.",
        japanese: "おんがくが きこ___。",
        answer: "えます",
        options: ["えます", "きます", "える", "きません"],
        hint: "きこえる is spontaneous potential",
        explanation: "きく → きこえる (can hear/is audible). おんがくが きこえます (I can hear music)."
      },
      {
        type: "build",
        prompt: "Can you write hiragana?",
        words: ["ひらがなが", "かけます", "か"],
        answer: ["ひらがなが", "かけます", "か"],
        hint: "かく potential form",
        explanation: "かく → かける (can write). ひらがなが かけますか (can you write hiragana?)."
      },
      {
        type: "fill",
        prompt: "I cannot do this job.",
        japanese: "この しごとは でき___。",
        answer: "ません",
        options: ["ません", "ます", "ない", "る"],
        hint: "できる negative form",
        explanation: "できる (can do) → できません (cannot do). この しごとは できません."
      },
      {
        type: "build",
        prompt: "Can you use chopsticks?",
        words: ["おはしが", "つかえます", "か"],
        answer: ["おはしが", "つかえます", "か"],
        hint: "つかう potential form",
        explanation: "つかう → つかえる (can use). おはしが つかえますか (can you use chopsticks?)."
      }
    ]
  },
  {
    id: 14,
    title: "Volitional Form (~よう)",
    book: "Genki II",
    chapter: 14,
    grammar: [
      { point: "Volitional form", explanation: "Let's do / I think I'll do" },
      { point: "~と思っています", explanation: "I'm thinking of doing" },
      { point: "~つもりです", explanation: "I intend to do" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "Let's go!",
        japanese: "いき___！",
        answer: "ましょう",
        options: ["ましょう", "ます", "ました", "よう"],
        hint: "ましょう is polite volitional",
        explanation: "いく → いきましょう (let's go). ましょう is the polite volitional form for suggestions."
      },
      {
        type: "build",
        prompt: "I'm thinking of studying abroad.",
        words: ["りゅうがく", "しよう", "と", "おもっています"],
        answer: ["りゅうがく", "しよう", "と", "おもっています"],
        hint: "Volitional + と + おもっています",
        explanation: "する → しよう (volitional). ~ようと おもっています means 'I'm thinking of doing'. りゅうがく しようと おもっています."
      },
      {
        type: "fill",
        prompt: "I intend to go to Japan next year.",
        japanese: "らいねん にほんに いく___です。",
        answer: "つもり",
        options: ["つもり", "よう", "こと", "もの"],
        hint: "つもり expresses intention",
        explanation: "~つもりです means 'intend to do'. いくつもりです (intend to go). Shows firm intention."
      },
      {
        type: "fill",
        prompt: "Let's eat lunch together.",
        japanese: "いっしょに ひるごはんを たべ___。",
        answer: "ましょう",
        options: ["ましょう", "ます", "よう", "たい"],
        hint: "Polite suggestion",
        explanation: "たべる → たべましょう (let's eat). Polite invitation or suggestion."
      },
      {
        type: "build",
        prompt: "I think I'll take a break.",
        words: ["やすもう", "と", "おもいます"],
        answer: ["やすもう", "と", "おもいます"],
        hint: "Volitional + と + おもいます",
        explanation: "やすむ → やすもう (volitional). ~ようと おもいます means 'I think I'll do'. やすもうと おもいます."
      },
      {
        type: "fill",
        prompt: "I intend to wake up early tomorrow.",
        japanese: "あした はやく おきる___です。",
        answer: "つもり",
        options: ["つもり", "よう", "こと", "たい"],
        hint: "Firm intention",
        explanation: "おきるつもりです expresses intention to wake up early. Shows determination."
      },
      {
        type: "build",
        prompt: "Let's watch a movie tonight.",
        words: ["こんばん", "えいがを", "みましょう"],
        answer: ["こんばん", "えいがを", "みましょう"],
        hint: "Time + object + volitional",
        explanation: "みる → みましょう. こんばん えいがを みましょう (let's watch a movie tonight)."
      },
      {
        type: "fill",
        prompt: "I'm thinking of buying a new car.",
        japanese: "あたらしい くるまを かおう___ おもっています。",
        answer: "と",
        options: ["と", "を", "に", "が"],
        hint: "Quote marker after volitional",
        explanation: "かう → かおう (volitional). かおうと おもっています (I'm thinking of buying)."
      },
      {
        type: "build",
        prompt: "I intend to study Japanese every day.",
        words: ["まいにち", "にほんごを", "べんきょうする", "つもりです"],
        answer: ["まいにち", "にほんごを", "べんきょうする", "つもりです"],
        hint: "Dictionary form + つもりです",
        explanation: "べんきょうするつもりです (intend to study). まいにち にほんごを べんきょうするつもりです."
      },
      {
        type: "fill",
        prompt: "Shall we go to the library?",
        japanese: "としょかんに いき___か。",
        answer: "ましょう",
        options: ["ましょう", "ます", "ました", "よう"],
        hint: "Polite suggestion in question form",
        explanation: "いきましょうか (shall we go?) is a polite suggestion asking for agreement."
      }
    ]
  },
  {
    id: 15,
    title: "Conditional Form (~たら)",
    book: "Genki II",
    chapter: 15,
    grammar: [
      { point: "~たら", explanation: "If/when (after verbs, adjectives)" },
      { point: "~たら、~", explanation: "Conditional + result" },
      { point: "~たらどう", explanation: "Why don't you / How about" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "If it rains tomorrow, I won't go.",
        japanese: "あした あめが ふっ___ いきません。",
        answer: "たら",
        options: ["たら", "て", "から", "と"],
        hint: "たら for conditional 'if/when'",
        explanation: "ふる → ふった → ふったら (if it rains). ~たら is the conditional form. ふったら いきません."
      },
      {
        type: "build",
        prompt: "Why don't you take a rest?",
        words: ["やすんだら", "どう", "です", "か"],
        answer: ["やすんだら", "どう", "です", "か"],
        hint: "~たらどうですか suggests advice",
        explanation: "やすむ → やすんだら. ~たらどうですか (why don't you/how about). やすんだら どうですか."
      },
      {
        type: "fill",
        prompt: "When I become free, I'll call you.",
        japanese: "ひまに なっ___ でんわします。",
        answer: "たら",
        options: ["たら", "て", "ば", "なら"],
        hint: "たら for temporal 'when'",
        explanation: "なる → なった → なったら (when/if I become). ひまに なったら でんわします."
      },
      {
        type: "fill",
        prompt: "If you turn right, you'll see a bank.",
        japanese: "みぎに まがっ___ ぎんこうが あります。",
        answer: "たら",
        options: ["たら", "て", "ば", "と"],
        hint: "Conditional for directions",
        explanation: "まがる → まがった → まがったら (if you turn). みぎに まがったら ぎんこうが あります."
      },
      {
        type: "build",
        prompt: "If it's cheap, I'll buy it.",
        words: ["やすかったら", "かいます"],
        answer: ["やすかったら", "かいます"],
        hint: "い-adjective + たら",
        explanation: "やすい → やすかった → やすかったら (if it's cheap). やすかったら かいます."
      },
      {
        type: "fill",
        prompt: "When spring comes, let's go see cherry blossoms.",
        japanese: "はるに なっ___ さくらを みに いきましょう。",
        answer: "たら",
        options: ["たら", "て", "ば", "と"],
        hint: "When something happens in future",
        explanation: "なる → なったら (when it becomes). はるに なったら (when spring comes)."
      },
      {
        type: "build",
        prompt: "If you don't understand, please ask.",
        words: ["わからなかったら", "きいて", "ください"],
        answer: ["わからなかったら", "きいて", "ください"],
        hint: "Negative adjective + たら",
        explanation: "わかる → わからない → わからなかった → わからなかったら (if you don't understand)."
      },
      {
        type: "fill",
        prompt: "Why don't you go to the hospital?",
        japanese: "びょういんに いっ___ どうですか。",
        answer: "たら",
        options: ["たら", "て", "ば", "と"],
        hint: "Suggestion pattern",
        explanation: "いく → いった → いったら. ~たらどうですか gives advice. いったら どうですか."
      },
      {
        type: "build",
        prompt: "If you press this button, the door will open.",
        words: ["この", "ボタンを", "おしたら", "ドアが", "あきます"],
        answer: ["この", "ボタンを", "おしたら", "ドアが", "あきます"],
        hint: "Cause and effect with たら",
        explanation: "おす → おした → おしたら (if you press). Cause-effect: おしたら ドアが あきます."
      },
      {
        type: "fill",
        prompt: "When I got home, my mother was there.",
        japanese: "うちに かえっ___ ははが いました。",
        answer: "たら",
        options: ["たら", "て", "ば", "と"],
        hint: "Past discovery/result",
        explanation: "かえる → かえった → かえったら (when I returned). かえったら ははが いました (discovered upon returning)."
      }
    ]
  },
  {
    id: 16,
    title: "Giving & Receiving (あげる/くれる/もらう)",
    book: "Genki II",
    chapter: 16,
    grammar: [
      { point: "あげる", explanation: "To give (I/we give to others)" },
      { point: "くれる", explanation: "To give (someone gives to me/us)" },
      { point: "もらう", explanation: "To receive" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I gave a present to my friend.",
        japanese: "ともだちに プレゼントを ___。",
        answer: "あげました",
        options: ["あげました", "くれました", "もらいました", "やりました"],
        hint: "あげる: I/we give to others",
        explanation: "あげる means 'to give' from the speaker's perspective. ともだちに プレゼントを あげました (I gave a present to my friend)."
      },
      {
        type: "build",
        prompt: "My friend gave me a book.",
        words: ["が", "ほんを", "くれました", "ともだち"],
        answer: ["ともだち", "が", "ほんを", "くれました"],
        hint: "くれる: someone gives to me/us",
        explanation: "くれる means 'to give to me/us'. ともだちが ほんを くれました (my friend gave me a book)."
      },
      {
        type: "fill",
        prompt: "I received money from my parents.",
        japanese: "りょうしんから おかねを ___。",
        answer: "もらいました",
        options: ["もらいました", "くれました", "あげました", "いただきました"],
        hint: "もらう: to receive from someone",
        explanation: "もらう means 'to receive'. りょうしんから おかねを もらいました (I received money from my parents)."
      },
      {
        type: "fill",
        prompt: "I gave flowers to my mother.",
        japanese: "ははに はなを ___。",
        answer: "あげました",
        options: ["あげました", "くれました", "もらいました", "さしあげました"],
        hint: "Giving to family members",
        explanation: "あげる is used when giving to others. ははに はなを あげました (I gave flowers to my mother)."
      },
      {
        type: "build",
        prompt: "My teacher gave me advice.",
        words: ["せんせいが", "アドバイスを", "くれました"],
        answer: ["せんせいが", "アドバイスを", "くれました"],
        hint: "Someone gave to me",
        explanation: "くれる indicates giving toward the speaker. せんせいが アドバイスを くれました."
      },
      {
        type: "fill",
        prompt: "I received a letter from Tanaka-san.",
        japanese: "たなかさんから てがみを ___。",
        answer: "もらいました",
        options: ["もらいました", "くれました", "あげました", "いただきました"],
        hint: "Receiving from someone",
        explanation: "もらう with から indicates the giver. たなかさんから てがみを もらいました."
      },
      {
        type: "build",
        prompt: "I will give this to you.",
        words: ["これを", "あなたに", "あげます"],
        answer: ["これを", "あなたに", "あげます"],
        hint: "I give to you",
        explanation: "あげる for giving away from speaker. これを あなたに あげます (I'll give this to you)."
      },
      {
        type: "fill",
        prompt: "My mother made dinner for me.",
        japanese: "ははが ばんごはんを つくって___。",
        answer: "くれました",
        options: ["くれました", "あげました", "もらいました", "いただきました"],
        hint: "Te-form + くれる for favor received",
        explanation: "~てくれる means 'did something for me'. つくってくれました (made for me)."
      },
      {
        type: "build",
        prompt: "I received a camera from my grandfather.",
        words: ["そふから", "カメラを", "もらいました"],
        answer: ["そふから", "カメラを", "もらいました"],
        hint: "Receiving from family",
        explanation: "もらう indicates receiving. そふから カメラを もらいました (received a camera from grandfather)."
      },
      {
        type: "fill",
        prompt: "I helped my younger brother.",
        japanese: "おとうとを てつだって___。",
        answer: "あげました",
        options: ["あげました", "くれました", "もらいました", "さしあげました"],
        hint: "Te-form + あげる for favor given",
        explanation: "~てあげる means 'did something for someone'. てつだってあげました (I helped him)."
      }
    ]
  },
  {
    id: 17,
    title: "~そうです (hearsay & appearance)",
    book: "Genki II",
    chapter: 17,
    grammar: [
      { point: "~そうです (hearsay)", explanation: "I heard that..." },
      { point: "~そうです (appearance)", explanation: "It looks like..." },
      { point: "~みたいです", explanation: "It seems like / looks like" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I heard it will rain tomorrow.",
        japanese: "あした あめが ふる___です。",
        answer: "そう",
        options: ["そう", "よう", "らしい", "みたい"],
        hint: "そうです for hearsay (what you heard)",
        explanation: "Plain form + そうです = 'I heard that...'. ふるそうです (I heard it will rain). Reports information."
      },
      {
        type: "fill",
        prompt: "This looks delicious.",
        japanese: "これは おいし___です。",
        answer: "そう",
        options: ["そう", "い", "らしい", "よう"],
        hint: "Adjective stem + そう for appearance",
        explanation: "おいしい → おいし (stem) + そう = 'looks delicious'. Based on visual appearance. おいしそうです."
      },
      {
        type: "build",
        prompt: "It seems like he's sick.",
        words: ["かれは", "みたい", "びょうき", "です"],
        answer: ["かれは", "びょうき", "みたい", "です"],
        hint: "みたい means 'seems like/looks like'",
        explanation: "~みたいです expresses resemblance or conjecture. かれは びょうきみたいです (seems like he's sick)."
      },
      {
        type: "fill",
        prompt: "It looks like it will break.",
        japanese: "われ___です。",
        answer: "そう",
        options: ["そう", "る", "た", "らしい"],
        hint: "Verb stem + そう for appearance",
        explanation: "われる → われ (stem) + そう = 'looks like it will break'. Based on observation. われそうです."
      },
      {
        type: "fill",
        prompt: "I heard the exam was difficult.",
        japanese: "しけんは むずかしかった___です。",
        answer: "そう",
        options: ["そう", "よう", "らしい", "みたい"],
        hint: "Plain past + そうです for hearsay",
        explanation: "むずかしかったそうです (I heard it was difficult). Reports what you heard from others."
      },
      {
        type: "build",
        prompt: "This bag looks heavy.",
        words: ["この", "かばんは", "おもそう", "です"],
        answer: ["この", "かばんは", "おもそう", "です"],
        hint: "い-adjective stem + そう",
        explanation: "おもい → おも (stem) + そう. この かばんは おもそうです (this bag looks heavy)."
      },
      {
        type: "fill",
        prompt: "He seems to be busy.",
        japanese: "かれは いそがしい___です。",
        answer: "みたい",
        options: ["みたい", "そう", "い", "らしい"],
        hint: "みたい follows plain form",
        explanation: "いそがしいみたいです (seems to be busy). みたい expresses inference based on observation."
      },
      {
        type: "build",
        prompt: "I heard she will come tomorrow.",
        words: ["かのじょは", "あした", "くる", "そうです"],
        answer: ["かのじょは", "あした", "くる", "そうです"],
        hint: "Plain form + そうです for hearsay",
        explanation: "くるそうです (I heard she will come). Reports secondhand information."
      },
      {
        type: "fill",
        prompt: "The food looks tasty.",
        japanese: "りょうりが おいし___です。",
        answer: "そう",
        options: ["そう", "い", "みたい", "らしい"],
        hint: "Visual impression of food",
        explanation: "おいしい → おいし + そう. おいしそうです (looks delicious) based on appearance."
      },
      {
        type: "build",
        prompt: "It seems like a good movie.",
        words: ["いい", "えいが", "みたい", "です"],
        answer: ["いい", "えいが", "みたい", "です"],
        hint: "Noun + みたい",
        explanation: "いい えいがみたいです (seems like a good movie). みたい expresses resemblance or supposition."
      }
    ]
  },
  {
    id: 18,
    title: "Honorific & Humble Speech",
    book: "Genki II",
    chapter: 18,
    grammar: [
      { point: "Honorific verbs", explanation: "Respectful forms for others' actions" },
      { point: "Humble verbs", explanation: "Humble forms for your own actions" },
      { point: "~ていらっしゃる", explanation: "Honorific progressive" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "The teacher is in the office. (honorific)",
        japanese: "せんせいは じむしつに ___。",
        answer: "いらっしゃいます",
        options: ["いらっしゃいます", "います", "おります", "ございます"],
        hint: "Honorific form of いる",
        explanation: "いる → いらっしゃる (honorific). Used to show respect for the teacher. いらっしゃいます."
      },
      {
        type: "fill",
        prompt: "I will go. (humble)",
        japanese: "わたしが ___。",
        answer: "まいります",
        options: ["まいります", "いきます", "いらっしゃいます", "おります"],
        hint: "Humble form of いく",
        explanation: "いく → まいる (humble). Used to lower yourself when speaking to superiors. まいります."
      },
      {
        type: "build",
        prompt: "What would you like to drink? (honorific)",
        words: ["なにを", "めしあがります", "お", "か"],
        answer: ["なにを", "お", "めしあがります", "か"],
        hint: "Honorific form of たべる/のむ",
        explanation: "のむ → おめしあがる (honorific). Shows respect to the person drinking. なにを おめしあがりますか."
      },
      {
        type: "fill",
        prompt: "Where do you live? (honorific)",
        japanese: "どちらに おすみ___か。",
        answer: "です",
        options: ["です", "ます", "になります", "でございます"],
        hint: "お + verb stem + です pattern",
        explanation: "すむ → おすみです (honorific pattern). どちらに おすみですか shows respect."
      },
      {
        type: "fill",
        prompt: "I am called Tanaka. (humble)",
        japanese: "たなかと ___。",
        answer: "もうします",
        options: ["もうします", "いいます", "おっしゃいます", "よびます"],
        hint: "Humble form of いう",
        explanation: "いう → もうす (humble). Used to lower yourself. たなかと もうします (I'm called Tanaka)."
      },
      {
        type: "build",
        prompt: "The president said that. (honorific)",
        words: ["しゃちょうが", "そう", "おっしゃいました"],
        answer: ["しゃちょうが", "そう", "おっしゃいました"],
        hint: "Honorific form of いう",
        explanation: "いう → おっしゃる (honorific). しゃちょうが そう おっしゃいました (the president said that)."
      },
      {
        type: "fill",
        prompt: "I will come tomorrow. (humble)",
        japanese: "あした ___。",
        answer: "うかがいます",
        options: ["うかがいます", "きます", "いらっしゃいます", "まいります"],
        hint: "Humble form of いく/くる (to visit)",
        explanation: "くる → うかがう (humble form when visiting). Shows humility. あした うかがいます."
      },
      {
        type: "build",
        prompt: "Do you know? (honorific)",
        words: ["ごぞんじ", "です", "か"],
        answer: ["ごぞんじ", "です", "か"],
        hint: "Honorific form of しっている",
        explanation: "しっている → ごぞんじです (honorific). ごぞんじですか (do you know?) shows respect."
      },
      {
        type: "fill",
        prompt: "I am at home. (humble)",
        japanese: "いまうちに ___。",
        answer: "おります",
        options: ["おります", "います", "いらっしゃいます", "ございます"],
        hint: "Humble form of いる",
        explanation: "いる → おる (humble). Used to show humility about yourself. おります."
      },
      {
        type: "build",
        prompt: "Please look at this. (honorific)",
        words: ["これを", "ごらんください"],
        answer: ["これを", "ごらんください"],
        hint: "Honorific request form of みる",
        explanation: "みる → ごらんになる (honorific). ごらんください (please look) is the honorific request form."
      }
    ]
  },
  {
    id: 19,
    title: "Causative Form (~させる)",
    book: "Genki II",
    chapter: 19,
    grammar: [
      { point: "Causative form", explanation: "To make/let someone do something" },
      { point: "~させる vs ~させられる", explanation: "Make do vs. be made to do" },
      { point: "Permission (~させてください)", explanation: "Please let me do" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "My mother made me clean my room.",
        japanese: "ははが わたしに へやを そうじ___。",
        answer: "させました",
        options: ["させました", "しました", "されました", "させられました"],
        hint: "Causative form of する",
        explanation: "する → させる (causative: make do). ははが そうじさせました (mother made me clean)."
      },
      {
        type: "build",
        prompt: "Please let me go home.",
        words: ["かえらせて", "ください", "うちに"],
        answer: ["うちに", "かえらせて", "ください"],
        hint: "Causative te-form + ください",
        explanation: "かえる → かえらせる → かえらせてください (please let me go home)."
      },
      {
        type: "fill",
        prompt: "The teacher made us write an essay.",
        japanese: "せんせいが さくぶんを かか___。",
        answer: "せました",
        options: ["せました", "きました", "されました", "せられました"],
        hint: "Causative form of かく",
        explanation: "かく → かかせる (causative). せんせいが かかせました (teacher made us write)."
      },
      {
        type: "fill",
        prompt: "Please let me try.",
        japanese: "やらせて___。",
        answer: "ください",
        options: ["ください", "ます", "ました", "あげます"],
        hint: "Request to be allowed to do something",
        explanation: "やる → やらせる → やらせてください (please let me try/do it)."
      },
      {
        type: "build",
        prompt: "My father let me use the car.",
        words: ["ちちが", "くるまを", "つかわせて", "くれました"],
        answer: ["ちちが", "くるまを", "つかわせて", "くれました"],
        hint: "Causative + てくれる for permission granted",
        explanation: "つかう → つかわせる. つかわせてくれました (let me use, as a favor)."
      },
      {
        type: "fill",
        prompt: "I made my younger sibling eat vegetables.",
        japanese: "おとうとに やさいを たべ___。",
        answer: "させました",
        options: ["させました", "ました", "られました", "てもらいました"],
        hint: "Causative form of たべる",
        explanation: "たべる → たべさせる (causative). やさいを たべさせました (made eat vegetables)."
      },
      {
        type: "build",
        prompt: "Please let me explain.",
        words: ["せつめい", "させて", "ください"],
        answer: ["せつめい", "させて", "ください"],
        hint: "する verb causative request",
        explanation: "せつめいする → せつめいさせる → せつめいさせてください (please let me explain)."
      },
      {
        type: "fill",
        prompt: "The boss made employees work overtime.",
        japanese: "しゃちょうが しゃいんに ざんぎょう___。",
        answer: "させました",
        options: ["させました", "しました", "されました", "してもらいました"],
        hint: "Causative with する verb",
        explanation: "ざんぎょうする → ざんぎょうさせる. Making someone do overtime work."
      },
      {
        type: "build",
        prompt: "My parents let me study abroad.",
        words: ["りょうしんが", "りゅうがく", "させて", "くれました"],
        answer: ["りょうしんが", "りゅうがく", "させて", "くれました"],
        hint: "Causative as permission with くれる",
        explanation: "りゅうがくさせてくれました (parents let me/allowed me to study abroad)."
      },
      {
        type: "fill",
        prompt: "Don't make the children cry.",
        japanese: "こどもを なか___ないでください。",
        answer: "せ",
        options: ["せ", "し", "させ", "され"],
        hint: "Negative causative request",
        explanation: "なく → なかせる (causative). なかせないでください (please don't make them cry)."
      }
    ]
  },
  {
    id: 20,
    title: "Passive Form (~される)",
    book: "Genki II",
    chapter: 20,
    grammar: [
      { point: "Passive form", explanation: "To be done to / affected by" },
      { point: "Adversative passive", explanation: "Suffering from someone's action" },
      { point: "~に~られる", explanation: "Passive construction" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I was scolded by my teacher.",
        japanese: "せんせいに しから___。",
        answer: "れました",
        options: ["れました", "りました", "られました", "られます"],
        hint: "Passive form of しかる",
        explanation: "しかる → しかられる (passive). せんせいに しかられました (was scolded by teacher)."
      },
      {
        type: "build",
        prompt: "My wallet was stolen.",
        words: ["が", "さいふ", "ぬすまれました"],
        answer: ["さいふ", "が", "ぬすまれました"],
        hint: "Passive of ぬすむ",
        explanation: "ぬすむ → ぬすまれる (passive). さいふが ぬすまれました (wallet was stolen)."
      },
      {
        type: "fill",
        prompt: "This book was written by Murakami.",
        japanese: "この ほんは むらかみさんに かか___。",
        answer: "れました",
        options: ["れました", "きました", "られました", "いました"],
        hint: "Passive form of かく",
        explanation: "かく → かかれる (passive). むらかみさんに かかれました (was written by Murakami)."
      },
      {
        type: "fill",
        prompt: "I was stepped on by someone on the train.",
        japanese: "でんしゃで だれかに あしを ふま___。",
        answer: "れました",
        options: ["れました", "りました", "られました", "せました"],
        hint: "Adversative passive (suffered from action)",
        explanation: "ふむ → ふまれる (passive). あしを ふまれました (had my foot stepped on)."
      },
      {
        type: "build",
        prompt: "Japanese is spoken in Japan.",
        words: ["にほんでは", "にほんごが", "はなされています"],
        answer: ["にほんでは", "にほんごが", "はなされています"],
        hint: "Passive describing general usage",
        explanation: "はなす → はなされる (passive). にほんごが はなされています (Japanese is spoken)."
      },
      {
        type: "fill",
        prompt: "I was asked a difficult question.",
        japanese: "むずかしい しつもんを きか___。",
        answer: "れました",
        options: ["れました", "きました", "られました", "いました"],
        hint: "Passive of きく",
        explanation: "きく → きかれる (passive). しつもんを きかれました (was asked a question)."
      },
      {
        type: "build",
        prompt: "The window was broken by the child.",
        words: ["こどもに", "まどが", "われました"],
        answer: ["こどもに", "まどが", "われました"],
        hint: "Passive with agent marked by に",
        explanation: "わる → われる (passive). こどもに まどが われました (window was broken by child)."
      },
      {
        type: "fill",
        prompt: "I was praised by my boss.",
        japanese: "じょうしに ほめ___。",
        answer: "られました",
        options: ["られました", "ました", "れました", "させました"],
        hint: "Passive of ほめる (ichidan verb)",
        explanation: "ほめる → ほめられる (passive). じょうしに ほめられました (was praised by boss)."
      },
      {
        type: "build",
        prompt: "My phone was used by my brother.",
        words: ["おとうとに", "けいたいを", "つかわれました"],
        answer: ["おとうとに", "けいたいを", "つかわれました"],
        hint: "Adversative passive (inconvenience)",
        explanation: "つかう → つかわれる. おとうとに けいたいを つかわれました (brother used my phone - to my inconvenience)."
      },
      {
        type: "fill",
        prompt: "This temple was built 500 years ago.",
        japanese: "この おてらは 500ねんまえに たて___。",
        answer: "られました",
        options: ["られました", "ました", "れました", "てました"],
        hint: "Passive of たてる",
        explanation: "たてる → たてられる (passive). 500ねんまえに たてられました (was built 500 years ago)."
      }
    ]
  },
  {
    id: 21,
    title: "~ば (Conditional)",
    book: "Genki II",
    chapter: 21,
    grammar: [
      { point: "~ば conditional", explanation: "If (general conditional)" },
      { point: "~なければ", explanation: "If not / unless" },
      { point: "~ば~ほど", explanation: "The more...the more..." }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "If you study, you'll pass.",
        japanese: "べんきょうすれ___ ごうかくします。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "ば conditional form",
        explanation: "する → すれば (if you do). べんきょうすれば ごうかくします (if you study, you'll pass)."
      },
      {
        type: "build",
        prompt: "The more I learn, the more interesting it becomes.",
        words: ["べんきょうすれば", "おもしろく", "するほど", "なります"],
        answer: ["べんきょうすれば", "するほど", "おもしろく", "なります"],
        hint: "~ば~ほど pattern",
        explanation: "~ば~ほど means 'the more...the more'. べんきょうすればするほど おもしろくなります."
      },
      {
        type: "fill",
        prompt: "If it's cheap, I'll buy it.",
        japanese: "やすけれ___ かいます。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "い-adjective ば form",
        explanation: "やすい → やすければ (if cheap). やすければ かいます (if it's cheap, I'll buy)."
      },
      {
        type: "fill",
        prompt: "If you don't hurry, you'll be late.",
        japanese: "いそがなけれ___ おくれます。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "Negative ば form",
        explanation: "いそがない → いそがなければ (if you don't hurry). いそがなければ おくれます."
      },
      {
        type: "build",
        prompt: "If you turn left, you'll see the station.",
        words: ["ひだりに", "まがれば", "えきが", "みえます"],
        answer: ["ひだりに", "まがれば", "えきが", "みえます"],
        hint: "ば for directions",
        explanation: "まがる → まがれば (if you turn). ひだりに まがれば えきが みえます."
      },
      {
        type: "fill",
        prompt: "The more you practice, the better you get.",
        japanese: "れんしゅうすれ___ するほど じょうずに なります。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "~ば~ほど construction",
        explanation: "れんしゅうすればするほど (the more you practice). Progressive improvement pattern."
      },
      {
        type: "build",
        prompt: "If you're tired, please rest.",
        words: ["つかれて", "いれば", "やすんで", "ください"],
        answer: ["つかれて", "いれば", "やすんで", "ください"],
        hint: "て-form + いれば",
        explanation: "つかれている → つかれていれば (if you are tired). つかれていれば やすんでください."
      },
      {
        type: "fill",
        prompt: "If it's quiet, I can study.",
        japanese: "しずかであれ___ べんきょうできます。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "な-adjective ば form",
        explanation: "しずか → しずかであれば (if it's quiet). しずかであれば べんきょうできます."
      },
      {
        type: "build",
        prompt: "If you don't eat, you won't get energy.",
        words: ["たべなければ", "げんきに", "なりません"],
        answer: ["たべなければ", "げんきに", "なりません"],
        hint: "Negative conditional",
        explanation: "たべない → たべなければ (if you don't eat). たべなければ げんきに なりません."
      },
      {
        type: "fill",
        prompt: "If spring comes, the flowers will bloom.",
        japanese: "はるに なれ___ はなが さきます。",
        answer: "ば",
        options: ["ば", "たら", "と", "なら"],
        hint: "なる conditional form",
        explanation: "なる → なれば (if it becomes). はるに なれば はなが さきます (when spring comes, flowers bloom)."
      }
    ]
  },
  {
    id: 22,
    title: "~のに (despite/although)",
    book: "Genki II",
    chapter: 22,
    grammar: [
      { point: "~のに", explanation: "Even though / despite" },
      { point: "Contrast with ~が", explanation: "Difference in nuance" },
      { point: "~のに + complaint", explanation: "Expressing dissatisfaction" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "Even though I studied, I failed.",
        japanese: "べんきょうした___ しっぱいしました。",
        answer: "のに",
        options: ["のに", "が", "から", "ので"],
        hint: "のに expresses unexpected result",
        explanation: "~のに shows contrast between expectation and result. べんきょうしたのに しっぱいしました (despite studying, I failed)."
      },
      {
        type: "build",
        prompt: "Despite being expensive, it's not good.",
        words: ["たかい", "のに", "よく", "ないです"],
        answer: ["たかい", "のに", "よく", "ないです"],
        hint: "い-adjective + のに",
        explanation: "たかいのに よくないです (despite being expensive, it's not good). Expresses disappointment."
      },
      {
        type: "fill",
        prompt: "Although I called, no one answered.",
        japanese: "でんわした___ だれも でませんでした。",
        answer: "のに",
        options: ["のに", "が", "けど", "から"],
        hint: "Unexpected outcome",
        explanation: "でんわしたのに (although I called). だれも でませんでした (no one answered). Contrary to expectation."
      },
      {
        type: "fill",
        prompt: "Even though it's Sunday, I have to work.",
        japanese: "にちようび___、しごとを しなければなりません。",
        answer: "なのに",
        options: ["なのに", "のに", "だから", "ので"],
        hint: "Noun + なのに",
        explanation: "にちようびなのに (even though it's Sunday). Noun requires な before のに."
      },
      {
        type: "build",
        prompt: "Although he promised, he didn't come.",
        words: ["やくそくした", "のに", "きませんでした"],
        answer: ["やくそくした", "のに", "きませんでした"],
        hint: "Past verb + のに",
        explanation: "やくそくしたのに きませんでした (although he promised, he didn't come). Broken expectation."
      },
      {
        type: "fill",
        prompt: "Despite being delicious, no one eats it.",
        japanese: "おいしい___ だれも たべません。",
        answer: "のに",
        options: ["のに", "から", "ので", "けど"],
        hint: "い-adjective + のに directly",
        explanation: "おいしいのに (despite being delicious). だれも たべません (no one eats it)."
      },
      {
        type: "build",
        prompt: "Even though I'm sick, I have to go to school.",
        words: ["びょうき", "なのに", "がっこうに", "いかなければなりません"],
        answer: ["びょうき", "なのに", "がっこうに", "いかなければなりません"],
        hint: "な-adjective + なのに",
        explanation: "びょうきなのに (even though I'm sick). がっこうに いかなければなりません (must go to school)."
      },
      {
        type: "fill",
        prompt: "Although I explained, he didn't understand.",
        japanese: "せつめいした___ わかりませんでした。",
        answer: "のに",
        options: ["のに", "が", "けど", "ので"],
        hint: "Despite effort, no result",
        explanation: "せつめいしたのに (although I explained). わかりませんでした (he didn't understand). Frustration implied."
      },
      {
        type: "build",
        prompt: "Despite being a holiday, the store is open.",
        words: ["きゅうじつ", "なのに", "みせが", "あいています"],
        answer: ["きゅうじつ", "なのに", "みせが", "あいています"],
        hint: "Noun + なのに",
        explanation: "きゅうじつなのに (despite being a holiday). みせが あいています (the store is open)."
      },
      {
        type: "fill",
        prompt: "Even though I'm tired, I can't sleep.",
        japanese: "つかれている___ ねむれません。",
        answer: "のに",
        options: ["のに", "から", "ので", "けど"],
        hint: "~ている form + のに",
        explanation: "つかれているのに (even though I'm tired). ねむれません (can't sleep). Ironic situation."
      }
    ]
  },
  {
    id: 23,
    title: "~ように (purpose/so that)",
    book: "Genki II",
    chapter: 23,
    grammar: [
      { point: "~ように", explanation: "So that / in order to (non-volitional)" },
      { point: "~ようになる", explanation: "Come to be able to" },
      { point: "~ようにする", explanation: "Make an effort to" }
    ],
    exercises: [
      {
        type: "fill",
        prompt: "I came early so as not to be late.",
        japanese: "おくれない___ はやく きました。",
        answer: "ように",
        options: ["ように", "ために", "よう", "ため"],
        hint: "ように for negative purpose",
        explanation: "おくれないように (so as not to be late). ように follows non-volitional or potential verbs."
      },
      {
        type: "build",
        prompt: "I've come to be able to speak Japanese.",
        words: ["にほんごが", "はなせる", "ように", "なりました"],
        answer: ["にほんごが", "はなせる", "ように", "なりました"],
        hint: "Potential + ように + なる",
        explanation: "はなせるようになりました (came to be able to speak). ~ようになる shows acquired ability."
      },
      {
        type: "fill",
        prompt: "I try to exercise every day.",
        japanese: "まいにち うんどうする___ しています。",
        answer: "ように",
        options: ["ように", "ために", "こと", "の"],
        hint: "ように + する for effort/habit",
        explanation: "うんどうするようにしています (I make an effort to exercise). ~ようにする shows conscious effort."
      },
      {
        type: "fill",
        prompt: "Please speak louder so I can hear.",
        japanese: "きこえる___ もっと おおきく はなしてください。",
        answer: "ように",
        options: ["ように", "ために", "ことに", "のに"],
        hint: "Potential verb + ように",
        explanation: "きこえるように (so that I can hear). ように used with potential verbs for purpose."
      },
      {
        type: "build",
        prompt: "I try not to eat too much.",
        words: ["たべすぎない", "ように", "しています"],
        answer: ["たべすぎない", "ように", "しています"],
        hint: "Negative + ように + する",
        explanation: "たべすぎないようにしています (I try not to eat too much). Conscious effort to avoid something."
      },
      {
        type: "fill",
        prompt: "I've become able to read kanji.",
        japanese: "かんじが よめる___ なりました。",
        answer: "ように",
        options: ["ように", "ことに", "ために", "のに"],
        hint: "Acquired ability pattern",
        explanation: "よめるようになりました (came to be able to read). ~ようになる indicates gradual change."
      },
      {
        type: "build",
        prompt: "I closed the window so it wouldn't be cold.",
        words: ["さむく", "ないように", "まどを", "しめました"],
        answer: ["さむく", "ないように", "まどを", "しめました"],
        hint: "Negative adjective + ように",
        explanation: "さむくないように (so it wouldn't be cold). Action taken to prevent unwanted state."
      },
      {
        type: "fill",
        prompt: "I got used to waking up early.",
        japanese: "はやく おきる___ なりました。",
        answer: "ように",
        options: ["ように", "ことに", "ために", "のに"],
        hint: "Habitual change with なる",
        explanation: "はやく おきるようになりました (came to wake up early). ~ようになる for developed habit."
      },
      {
        type: "build",
        prompt: "Please write clearly so everyone can read it.",
        words: ["みんなが", "よめる", "ように", "はっきり", "かいてください"],
        answer: ["みんなが", "よめる", "ように", "はっきり", "かいてください"],
        hint: "Purpose + request",
        explanation: "みんなが よめるように (so everyone can read). はっきり かいてください (please write clearly)."
      },
      {
        type: "fill",
        prompt: "I try to sleep before midnight.",
        japanese: "よなかまえに ねる___ しています。",
        answer: "ように",
        options: ["ように", "ために", "ことに", "のに"],
        hint: "Conscious effort pattern",
        explanation: "ねるようにしています (I try to sleep). ~ようにする indicates making an effort/habit."
      }
    ]
  }
];

// Helper function to get all exercises from all lessons
export const getAllExercises = () => {
  return lessons.flatMap((lesson, lessonIndex) =>
    lesson.exercises.map((exercise, exerciseIndex) => ({
      ...exercise,
      lessonId: lesson.id,
      lessonTitle: lesson.title,
      book: lesson.book,
      grammarPoints: lesson.grammar
    }))
  );
};

// Helper function to get random exercises
export const getRandomExercises = (pool, count) => {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

// Get exercises for a specific lesson
export const getLessonExercises = (lessonId) => {
  const lesson = lessons.find(l => l.id === lessonId);
  return lesson ? lesson.exercises : [];
};

export default lessons;
